import Nav from "@/components/welp/Nav";
import PageBg from "@/components/welp/PageBg";
import { Link } from "react-router-dom";

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A]">{title}</h2>
      <div className="mt-4 space-y-4 text-[#1A1A1A] text-lg font-medium leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export function TermsPage() {
  return (
    <PageBg>
      <Nav />
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20" data-testid="terms-page">
        <div className="text-sm uppercase tracking-[0.32em] mb-3 font-bold">
          <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#FF1A82]">Dating With Integrity</span>
        </div>
        <div className="text-veil">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A]">Terms &amp; Conditions</h1>
        <p className="mt-4 text-lg text-[#5A5A5A] font-semibold">Last updated: August 2026</p>
        </div>

        <Section title="1. Acceptance of Terms">
          <p>By accessing or using Welp (welp3x3.com, the "Service"), you agree to be bound by these Terms &amp; Conditions and our Privacy Policy. If you do not agree, do not use the Service.</p>
          <p>You must be at least 18 years old to create an account or submit any content.</p>
        </Section>

        <Section title="2. The Service">
          <p>Welp is a moderated platform that lets users submit structured experiences about people they have interacted with on third-party dating and social platforms.</p>
          <p>Profiles on Welp are created only when a review is submitted. Welp does not host empty profiles. Welp is not affiliated with, endorsed by, or connected to any third-party dating service.</p>
        </Section>

        <Section title="3. Account Verification vs. Content Verification">
          <p><strong>Welp verifies user accounts but does not independently verify the factual accuracy of user-submitted experiences.</strong> Every account must complete phone verification, which confirms the account belongs to a real person — it does not confirm the truth of what that person writes.</p>
          <p>User submissions represent the opinions and experiences of the submitting user. Welp does not independently verify the accuracy of user-generated content. Submissions may be reviewed, rejected, edited for policy compliance, or removed if they violate platform rules.</p>
          <p>Welp does not influence reviews in any way and is not responsible for their accuracy, completeness, or any outcomes resulting from them.</p>
        </Section>

        <Section title="4. User Conduct">
          <p>You agree NOT to submit content that contains:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Harassment, threats, or intimidation</li>
            <li>Hate speech or discriminatory language</li>
            <li>Doxxing (real names, addresses, phone numbers, exact workplaces, or other identifying information)</li>
            <li>Personal attacks or defamatory statements</li>
            <li>Explicit sexual content</li>
            <li>Content that violates any applicable law</li>
          </ul>
          <p>Written submissions may be processed using third-party artificial intelligence services for moderation, safety review, and summarization before publication. Welp reserves the right to remove content and suspend accounts at its discretion.</p>
        </Section>

        <Section title="5. Accountability System">
          <p>Users are limited to no more than three (3) rejected reviews in a row. After three consecutive rejections, the user receives a "ding". After three dings, the account is permanently banned from the Service.</p>
        </Section>

        <Section title="6. Account &amp; Password Security">
          <p>You are responsible for maintaining the confidentiality of your account credentials. Passwords must be at least 8 characters and contain at least one uppercase letter, one lowercase letter, one number, and one special character. You can reset a forgotten password through the "Forgot your password?" link on the sign-in page — a reset code will be texted to the verified phone number on your account.</p>
          <p>You are responsible for all activity on your account. Notify Welp immediately of any unauthorized use.</p>
        </Section>

        <Section title="7. Phone Verification &amp; Text Messages">
          <p>Every Welp account requires a verified mobile phone number. By registering, you agree to receive a one-time verification text message. Welp also sends transactional text messages (for example: review notifications, responses to your reviews, claim updates, and password reset codes). Message and data rates may apply. Welp does not send marketing text messages. Your phone number is never displayed publicly.</p>
        </Section>

        <Section title="8. Review Retention">
          <p>Once a review is submitted, it cannot be edited or deleted by the submitting user. By submitting a review you affirm that the content is true and accurate to the best of your knowledge, and you understand that Welp is not responsible for the information or any outcomes resulting from it.</p>
          <p>User-generated reviews may be retained when necessary for platform integrity, safety, dispute resolution, or legal compliance.</p>
        </Section>

        <Section title="9. Reviewed Person Rights">
          <p>Any person who is the subject of a review may claim their profile, view the review, respond to it, and optionally submit a reciprocal review of the original reviewer using the same review process.</p>
        </Section>

        <Section title="10. Review Disputes &amp; Appeals">
          <p>If you believe a review about you is incorrect or violates these Terms, you may dispute it. Contact us through the <Link to="/contact" className="text-[#10B981] underline">contact form</Link> or email <a href="mailto:Welp3x3@gmail.com" className="text-[#10B981] underline">Welp3x3@gmail.com</a> with the profile link and a description of the issue. We handle the following categories:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Mistaken identity</strong> — you believe a review was posted about the wrong person. Claim the profile and dispute the content; include anything that helps us confirm the mix-up.</li>
            <li><strong>Impersonation</strong> — someone claimed a profile that is actually you. Use the "Dispute this claim" button on the profile, which reverts it to unclaimed for review.</li>
            <li><strong>Minors</strong> — if any content involves a person under 18, report it immediately. It will be removed as a priority and the submitting account will be banned.</li>
            <li><strong>Illegal content</strong> — content that violates the law is removed once verified and, where required, reported to the appropriate authorities.</li>
            <li><strong>Private information</strong> — reviews containing addresses, phone numbers, workplaces, or other doxxing are removed and the submitter is subject to the accountability system.</li>
            <li><strong>Court orders</strong> — Welp complies with valid court orders and legally binding removal requests. Send legal documents to <a href="mailto:Welp3x3@gmail.com" className="text-[#10B981] underline">Welp3x3@gmail.com</a>.</li>
          </ul>
          <p><strong>Investigation process:</strong> every dispute is reviewed by a human administrator. We examine the reported content, the submitting account, moderation history, and any evidence you provide. Content that violates these Terms is removed; content that does not is retained with an explanation to you.</p>
          <p><strong>Response time:</strong> we aim to acknowledge every dispute within 72 hours and resolve most disputes within 14 days. Reports involving minors or safety are prioritized immediately.</p>
          <p><strong>Appeals:</strong> if you disagree with a dispute decision, reply to the decision message within 30 days with any new information. A second review will be conducted and the outcome of that appeal is final.</p>
        </Section>

        <Section title="11. Intellectual Property">
          <p>All Welp branding, layout, and original written content (excluding user-submitted content) is owned by Welp. You may not copy, modify, or redistribute it without written permission.</p>
          <p>By submitting content to Welp, you grant Welp a non-exclusive, royalty-free, worldwide license to host, display, and moderate that content on the Service.</p>
        </Section>

        <Section title="12. Disclaimers &amp; Limitation of Liability">
          <p>The Service is provided "as is" without warranties of any kind. To the maximum extent permitted by law, Welp shall not be liable for any indirect, incidental, consequential, or punitive damages arising from your use of the Service or any content posted on it.</p>
        </Section>

        <Section title="13. Termination">
          <p>Welp may suspend or terminate your access at any time for violation of these Terms. You may close your account at any time by contacting support.</p>
        </Section>

        <Section title="14. Governing Law">
          <p>This Agreement is governed by the laws of the State of Colorado, without regard to conflict-of-law principles. Disputes will be resolved in the state or federal courts located in Colorado.</p>
        </Section>

        <Section title="15. Changes to These Terms">
          <p>Welp may update these Terms at any time. Continued use of the Service after changes constitutes acceptance of the revised Terms.</p>
        </Section>

        <Section title="16. Contact">
          <p>Questions about these Terms can be sent to <a href="mailto:Welp3x3@gmail.com" className="text-[#10B981] underline">Welp3x3@gmail.com</a>.</p>
        </Section>

        <div className="mt-12 text-lg font-semibold">
          <Link to="/privacy" className="text-[#FF1A82] hover:underline">Read the Privacy Policy →</Link>
        </div>
      </article>
    </PageBg>
  );
}

export function PrivacyPage() {
  return (
    <PageBg>
      <Nav />
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20" data-testid="privacy-page">
        <div className="text-sm uppercase tracking-[0.32em] mb-3 font-bold">
          <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#FF1A82]">Dating With Integrity</span>
        </div>
        <div className="text-veil">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A]">Privacy Policy</h1>
        <p className="mt-4 text-lg text-[#5A5A5A] font-semibold">Last updated: August 2026</p>
        </div>

        <Section title="1. Information We Collect">
          <p>When you create a Welp account, we collect: email address, display name, and an encrypted (bcrypt-hashed) password. If you sign in with Google, we receive your basic Google profile.</p>
          <p>We also collect your <strong>mobile phone number</strong>, <strong>SMS verification information</strong> (one-time codes and delivery status), and your <strong>verification status</strong>. Phone verification is required for every account.</p>
          <p>When you submit a review, response, or story, we store the content along with the time it was submitted and the account that submitted it. We never share your identity with the public — only the reviewer and the reviewed person know who wrote which review.</p>
          <p>We also collect anonymous traffic data: page paths visited, referrer, and a salted hash of your IP + user agent (truncated, non-reversible). We use this only to count visitors and improve the Service.</p>
        </Section>

        <Section title="2. How We Use Your Phone Number">
          <p>Your phone number is used for:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Identity verification (one-time SMS verification codes)</li>
            <li>Account security (password reset codes)</li>
            <li>Review and response notifications</li>
            <li>Fraud prevention (one verified number per account)</li>
          </ul>
          <p><strong>Phone numbers are never publicly displayed. Phone numbers are never sold. Phone numbers are only shared with trusted service providers necessary to operate Welp</strong> (see Third-Party Services below). Numbers are stored securely alongside your account data.</p>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-disc pl-6 space-y-1">
            <li>To operate, maintain, and improve the Service</li>
            <li>To compute the Statistics &amp; Skinny summary on each profile</li>
            <li>To send transactional text messages (verification codes, review notifications, password reset codes) and, where necessary, transactional emails</li>
            <li>To enforce our community guidelines (AI moderation, ding system)</li>
            <li>To detect abuse and protect the Service</li>
          </ul>
        </Section>

        <Section title="4. AI Moderation">
          <p>Written submissions may be processed using third-party artificial intelligence services for moderation, safety review, and summarization. Text is sent to the AI provider solely for these purposes. Refer to the provider's own privacy policy for its data handling practices.</p>
        </Section>

        <Section title="5. What We Do NOT Do">
          <ul className="list-disc pl-6 space-y-1">
            <li>We do not sell, rent, or trade your personal information — including your phone number.</li>
            <li>We do not show your identity or phone number to the public — reviews are anonymous to other users.</li>
            <li>We do not independently verify the factual accuracy of user-submitted content.</li>
            <li>We do not track you across other websites.</li>
            <li>We do not send marketing text messages or marketing emails.</li>
          </ul>
        </Section>

        <Section title="6. Cookies &amp; Storage">
          <p>Welp uses a session cookie (or a JWT in browser local storage) so we can keep you signed in. We also store a small visitor hash (as described above) to count site traffic. We do not use third-party advertising cookies.</p>
        </Section>

        <Section title="7. Text Message &amp; Email Communications">
          <p>Welp communicates with members primarily by <strong>text message (SMS)</strong>: verification codes, password reset codes, review notifications, responses, and claim updates. Message and data rates may apply. We do not send marketing texts.</p>
          <p>Limited transactional email may be used where SMS is unavailable (for example, contacting a non-member who used the contact form). Emails are sent from <span className="text-[#10B981]">whatsup@welp3x3.com</span>; replies go to <a href="mailto:Welp3x3@gmail.com" className="text-[#10B981] underline">Welp3x3@gmail.com</a>.</p>
        </Section>

        <Section title="8. Data Retention">
          <p>User-generated reviews may be retained when necessary for platform integrity, safety, dispute resolution, or legal compliance. Account data is retained while your account is active. Password-reset codes expire after 15 minutes. Visitor traffic logs are retained for analytics for up to 24 months.</p>
        </Section>

        <Section title="9. Your Rights (GDPR / CCPA)">
          <p>If you live in the European Economic Area, the UK, or California, you have the right to access, correct, or delete the personal information we hold about you, and to object to certain uses. To exercise these rights, email <a href="mailto:Welp3x3@gmail.com" className="text-[#10B981] underline">Welp3x3@gmail.com</a>. Reviews you have submitted may be retained when necessary for platform integrity, safety, dispute resolution, or legal compliance — see the Review Disputes &amp; Appeals section of our <Link to="/terms" className="text-[#10B981] underline">Terms</Link>.</p>
        </Section>

        <Section title="10. Children">
          <p>Welp is not intended for anyone under 18. We do not knowingly collect information from minors.</p>
        </Section>

        <Section title="11. Security">
          <p>Passwords are stored as bcrypt hashes. Communication with the Service uses HTTPS. We follow industry-standard practices, but no system is perfectly secure — please use a strong unique password.</p>
        </Section>

        <Section title="12. Third-Party Services">
          <p>We use the following providers to operate the Service. Each processes data according to its own privacy policy:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>MongoDB Atlas — primary data storage</li>
            <li>Textbelt — SMS delivery for verification codes and text notifications (processes your phone number and message content)</li>
            <li>Resend — transactional email delivery</li>
            <li>Third-party artificial intelligence services — content moderation, safety review, and summarization</li>
            <li>Stripe — payment processing for paid features (we never see or store your card number)</li>
            <li>ipwho.is — anonymous IP geolocation for traffic analytics</li>
          </ul>
        </Section>

        <Section title="13. Changes to This Policy">
          <p>We may update this Privacy Policy at any time. Continued use of the Service after changes means you accept the updated policy.</p>
        </Section>

        <Section title="14. Contact">
          <p>Questions about this Privacy Policy can be sent to <a href="mailto:Welp3x3@gmail.com" className="text-[#10B981] underline">Welp3x3@gmail.com</a>.</p>
        </Section>

        <div className="mt-12 text-lg font-semibold">
          <Link to="/terms" className="text-[#FF1A82] hover:underline">Read the Terms &amp; Conditions →</Link>
        </div>
      </article>
    </PageBg>
  );
}
